import { useContext, useEffect, useState } from 'react';
import { CartContext } from '../components/providers/cart';
import { QuestionnairePageEventDocument } from '../graphql/graphql';
import { useQuery } from '@apollo/client';
import { A1_POWEREDBY_COLOR_PRIMARY, EVENTS_URL } from '../config';
import HeaderJoyComponent from '../components/common/HeaderJoy.component';
import { Box, CircularProgress, Container, Stack, Typography } from '@mui/joy';
import { formatPrimaryColor } from '../utils/formatter';
import { isReallyEmpty } from '../utils/validators';
import QuestionnaireLoaded from '../components/questionnaire/questionnaireLoaded';
import { ThemeProvider } from '../ThemeProvider';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PaymentTimer from '../components/payment/paymentTimerComponent';
import { ErrorAlert } from '../components/common/styled/HeaderJoy.styled';
import { Redirect } from 'react-router-dom';

export default function QuestionnairePage() {
    const [errorAlert, setErrorAlert] = useState<string | null>(null);

    const { currentEventId, cart } = useContext(CartContext);

    const { loading, data, error } = useQuery(QuestionnairePageEventDocument, {
        variables: {
            eventId: currentEventId!,
        },
        skip: !currentEventId,
    });

    useEffect(() => {
        if (!currentEventId) {
            window.location.href = EVENTS_URL;
        }
    }, [currentEventId]);

    if (!loading && error) {
        return <>Error Loading Data</>;
    }

    const organizerThemeColor = data?.event?.organizerColor || A1_POWEREDBY_COLOR_PRIMARY;

    const organizerPrimaryColor = formatPrimaryColor(
        data?.event?.eventType,
        data?.event?.organizerType,
        organizerThemeColor
    );

    const hasAdditionalTerms =
        data?.event?.additionalTerms != null && !isReallyEmpty(data?.event?.additionalTerms);

    const questions = data?.event?.questionnaire?.questions ?? [];

    const hasQuestionnaire =
        (data?.event?.questionnaire && data?.event?.questionnaire?.questions.length > 0) || false;

    const filteredQuestions = questions.filter((q) => {
        if (q?.ticketCategoryIds?.length === 0) {
            return true;
        }

        const selectedTicket = Object.keys(cart.quantities);

        const hasMatch = q?.ticketCategoryIds?.some((cat) => {
            return selectedTicket.includes(cat);
        });

        if (hasMatch) {
            return true;
        }

        return false;
    });

    const skipPage = filteredQuestions.length === 0 || Object.keys(cart.quantities).length === 0;

    return (
        <ThemeProvider>
            {loading ? (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100vh',
                        }}
                    >
                        <CircularProgress sx={{ color: '#000' }} />
                    </Box>
                </>
            ) : (
                <>
                    {!hasAdditionalTerms && (
                        <PaymentTimer organizerPrimaryColor={organizerPrimaryColor} />
                    )}

                    <Container
                        sx={{
                            paddingBottom: 7.5,
                            paddingTop: hasAdditionalTerms ? 3 : { xs: 0, md: 15 },
                        }}
                    >
                        <HeaderJoyComponent
                            hold={cart?.hold}
                            hasAdditionalTerms={hasAdditionalTerms}
                            previousPage={`/${data?.event?.shortEventId}`}
                            step={2}
                            organizerPrimaryColor={organizerPrimaryColor}
                            headerTitle={'Questionnaire'}
                            hasQuestionnaire={hasQuestionnaire}
                        />

                        <Box>
                            <Typography level="title-md" sx={{ mt: 0, mb: 4 }}>
                                To proceed, the organizer needs more information. Any personal
                                details will remain private.
                            </Typography>
                        </Box>

                        {errorAlert && (
                            <ErrorAlert>
                                <Box sx={{ display: 'flex' }}>
                                    <ErrorOutlineIcon sx={{ marginRight: '10px' }} />
                                    <Stack>
                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                margin: '0 0 10px 0',
                                                color: '#fff',
                                                fontWeight: 700,
                                            }}
                                        >
                                            Validation Failed
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', color: '#fff' }}>
                                            {errorAlert}
                                        </Typography>
                                    </Stack>
                                </Box>
                                <CloseIcon onClick={() => setErrorAlert(null)} />
                            </ErrorAlert>
                        )}

                        {data?.event?.questionnaire && !skipPage ? (
                            <QuestionnaireLoaded
                                event={data?.event}
                                questions={filteredQuestions}
                                organizerPrimaryColor={organizerPrimaryColor}
                                setErrorAlert={setErrorAlert}
                                hasAdditionalTerms={hasAdditionalTerms}
                            />
                        ) : (
                            <>
                                {hasAdditionalTerms ? (
                                    <Redirect to={'/additional-terms'} />
                                ) : (
                                    <Redirect to={'/payment'} />
                                )}
                            </>
                        )}
                    </Container>
                </>
            )}
        </ThemeProvider>
    );
}
